/**
 * ExchangeWidget.jsx
 * Compact Exchange opportunities widget for partner portal dashboards.
 * Shows top open requests for the provider's role. Front-and-center placement.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Zap, MapPin, Clock, Send, ChevronRight, RefreshCw, Lock,
  DollarSign, Hammer, Home, FileCheck, MessageSquare, Plus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  lender:     { label: "Funding",    color: "text-blue-400",    border: "border-blue-500/30",    bg: "bg-blue-500/10",    icon: DollarSign, accentBg: "bg-blue-500/[0.06]", accentBorder: "border-blue-500/20" },
  contractor: { label: "Contractor", color: "text-orange-400",  border: "border-orange-500/30",  bg: "bg-orange-500/10",  icon: Hammer,     accentBg: "bg-orange-500/[0.06]", accentBorder: "border-orange-500/20" },
  realtor:    { label: "Realtor",    color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10", icon: Home,       accentBg: "bg-emerald-500/[0.06]", accentBorder: "border-emerald-500/20" },
  title:      { label: "Title",      color: "text-purple-400",  border: "border-purple-500/30",  bg: "bg-purple-500/10",  icon: FileCheck,  accentBg: "bg-purple-500/[0.06]", accentBorder: "border-purple-500/20" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getKeyMetric(req) {
  const m = req.metadata || {};
  switch (req.request_type) {
    case "lender":     return m.loan_amount ? `$${Number(m.loan_amount).toLocaleString()}` : null;
    case "contractor": return m.budget_max  ? `Up to $${Number(m.budget_max).toLocaleString()}` : null;
    case "realtor":    return m.expected_price ? `$${Number(m.expected_price).toLocaleString()}` : null;
    case "title":      return m.transaction_type?.replace(/_/g, " ") || null;
    default: return null;
  }
}

// ── Inline Submit Offer Modal (compact) ──────────────────────────────────────
function QuickOfferModal({ open, onClose, request, role, onSubmitted }) {
  const [message, setMessage] = useState("");
  const [terms, setTerms] = useState({});
  const [saving, setSaving] = useState(false);
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.lender;
  const setT = (k, v) => setTerms(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!message.trim() || message.length < 10) { toast.error("Message must be at least 10 characters"); return; }
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/exchange/requests/${request.request_id}/respond`,
        { message, terms },
        { withCredentials: true }
      );
      toast.success("Offer submitted!");
      onSubmitted();
      onClose();
      setMessage(""); setTerms({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit offer");
    } finally {
      setSaving(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Send className={`w-4 h-4 ${cfg.color}`} /> Submit Offer
          </DialogTitle>
          <p className="text-xs text-white/40 line-clamp-1 mt-1">{request.title}</p>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {role === "lender" && (
            <div className="grid grid-cols-2 gap-3 p-3 border border-blue-500/20 bg-blue-500/5 rounded">
              {[["loan_amount","Loan Amount ($)"],["interest_rate","Interest Rate (%)"],["term_months","Term (mo)"],["ltv","LTV (%)"]].map(([k,l]) => (
                <div key={k}>
                  <Label className="text-[10px] text-white/40 mb-1 block">{l}</Label>
                  <Input type="number" value={terms[k]||""} onChange={e=>setT(k,e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-sm" />
                </div>
              ))}
            </div>
          )}
          {role === "contractor" && (
            <div className="grid grid-cols-2 gap-3 p-3 border border-orange-500/20 bg-orange-500/5 rounded">
              {[["bid_amount","Bid Amount ($)"],["timeline_days","Timeline (days)"]].map(([k,l]) => (
                <div key={k}>
                  <Label className="text-[10px] text-white/40 mb-1 block">{l}</Label>
                  <Input type="number" value={terms[k]||""} onChange={e=>setT(k,e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-sm" />
                </div>
              ))}
            </div>
          )}
          {role === "realtor" && (
            <div className="grid grid-cols-2 gap-3 p-3 border border-emerald-500/20 bg-emerald-500/5 rounded">
              {[["commission_pct","Commission (%)"],["timeline_days","Timeline (days)"]].map(([k,l]) => (
                <div key={k}>
                  <Label className="text-[10px] text-white/40 mb-1 block">{l}</Label>
                  <Input type="number" value={terms[k]||""} onChange={e=>setT(k,e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-sm" />
                </div>
              ))}
            </div>
          )}
          {role === "title" && (
            <div className="p-3 border border-purple-500/20 bg-purple-500/5 rounded">
              <Label className="text-[10px] text-white/40 mb-1 block">Estimated Fees ($)</Label>
              <Input type="number" value={terms.fees||""} onChange={e=>setT("fees",e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-sm" />
            </div>
          )}
          <div>
            <Label className="text-xs text-white/60 mb-1.5 block">Message *</Label>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Introduce yourself and explain why you're the right fit..."
              rows={3}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10 text-white/50 text-sm h-9">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex-1 font-bold text-sm h-9 text-white border ${cfg.border} ${cfg.bg} hover:opacity-90`}
            >
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ExchangeWidget ───────────────────────────────────────────────────────
/**
 * @param {string}  role       - "lender" | "contractor" | "realtor" | "title"
 * @param {object}  user       - current user object (for role check)
 * @param {string}  [className] - additional class names
 */
export default function ExchangeWidget({ role, user, className = "" }) {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerTarget, setOfferTarget] = useState(null);
  const [hasRole, setHasRole] = useState(false);

  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.lender;
  const Icon = cfg.icon;

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/exchange/opportunities/${role}`,
        { withCredentials: true }
      );
      setOpportunities(res.data.opportunities?.slice(0, 4) || []);
      setHasRole(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setHasRole(false);
      }
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  // ── Locked state ─────────────────────────────────────────────────────────
  if (!loading && !hasRole) {
    return (
      <div className={`border border-white/[0.06] bg-[#050505] ${className}`}>
        <div className="p-4 flex items-center gap-3">
          <div className={`w-9 h-9 rounded ${cfg.bg} ${cfg.border} border flex items-center justify-center flex-shrink-0`}>
            <Lock className={`w-4 h-4 ${cfg.color}`} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Exchange Opportunities</p>
            <p className="text-[11px] text-white/40">Add the {cfg.label} role to see live opportunities</p>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/upgrade")}
            className={`h-8 px-3 text-xs ${cfg.bg} ${cfg.border} border ${cfg.color} hover:opacity-90 shrink-0`}
          >
            Add Role
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`border ${cfg.accentBorder} ${cfg.accentBg} ${className}`} data-testid={`exchange-widget-${role}`}>
      {/* Widget header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded ${cfg.bg} border ${cfg.border} flex items-center justify-center`}>
            <Zap className={`w-3.5 h-3.5 ${cfg.color}`} />
          </div>
          <div>
            <p className="text-[13px] font-bold text-white">Exchange Opportunities</p>
            <p className="text-[10px] text-white/30">
              {loading ? "Loading..." : `${opportunities.length} open for you`}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/network/exchange?type=${role}`)}
          data-testid={`exchange-widget-view-all-${role}`}
          className={`flex items-center gap-1 text-[11px] font-medium ${cfg.color} hover:opacity-80 transition-opacity`}
        >
          View All <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Opportunities list */}
      <div className="divide-y divide-white/[0.03]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-white/20" />
          </div>
        ) : opportunities.length === 0 ? (
          <div className="py-6 text-center">
            <Icon className={`w-8 h-8 mx-auto mb-2 opacity-20 ${cfg.color}`} />
            <p className="text-xs text-white/30">No open requests right now</p>
            <p className="text-[11px] text-white/20 mt-0.5">Check back soon or post a note on the Exchange</p>
          </div>
        ) : (
          opportunities.map(opp => {
            const metric = getKeyMetric(opp);
            return (
              <div
                key={opp.request_id}
                data-testid={`widget-opp-${opp.request_id}`}
                className="px-4 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-white line-clamp-1 flex-1">{opp.title}</p>
                    {metric && <span className={`text-[11px] font-bold ${cfg.color} shrink-0`}>{metric}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="inline-flex items-center gap-1 text-[10px] text-white/30">
                      <MapPin className="w-2.5 h-2.5" />{opp.location}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-white/20">
                      <Clock className="w-2.5 h-2.5" />{timeAgo(opp.created_at)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-white/20">
                      <MessageSquare className="w-2.5 h-2.5" />{opp.response_count || 0}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setOfferTarget(opp)}
                  data-testid={`widget-submit-offer-${opp.request_id}`}
                  className={`h-7 px-2.5 text-[11px] shrink-0 border ${cfg.border} ${cfg.bg} ${cfg.color} hover:opacity-90`}
                >
                  <Send className="w-3 h-3 mr-1" /> Offer
                </Button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer CTA */}
      {!loading && opportunities.length > 0 && (
        <div className="px-4 py-2.5 border-t border-white/[0.04] flex items-center justify-between">
          <span className="text-[10px] text-white/20">Live deal-linked opportunities</span>
          <button
            onClick={() => navigate(`/network/exchange?type=${role}`)}
            className={`text-[11px] font-medium ${cfg.color} hover:opacity-80 flex items-center gap-1`}
          >
            Browse Exchange <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Quick Offer Modal */}
      <QuickOfferModal
        open={!!offerTarget}
        onClose={() => setOfferTarget(null)}
        request={offerTarget}
        role={role}
        onSubmitted={() => { fetchOpportunities(); setOfferTarget(null); }}
      />
    </div>
  );
}
